import React, { useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faBullseye, faClock, faDownload, faMinus, faStar, faTrophy, faUsers } from '@fortawesome/free-solid-svg-icons';

const StaffPerformancePage: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  const breadcrumb = [{ label: 'Zaměstnanci' }, { label: 'Výkonnost' }];

  const staff = [
    { id: '1', name: 'Anna Svobodová', role: 'Kadeřnice', avatar: 'https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-1.jpg' },
    { id: '2', name: 'Petr Novák', role: 'Kadeřník', avatar: 'https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-2.jpg' },
    { id: '3', name: 'Marie Krásná', role: 'Kadeřnice', avatar: 'https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-5.jpg' },
    { id: '4', name: 'Jana Nová', role: 'Kosmetička', avatar: 'https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-3.jpg' },
  ];

  const performanceData = [
    {
      staffId: '1',
      month: '2024-11',
      metrics: {
        totalServices: 156,
        totalRevenue: 125000,
        averageRating: 4.8,
        customerSatisfaction: 95,
        repeatCustomers: 78,
        newCustomers: 23,
        workingHours: 168,
        efficiency: 92,
      },
      comparison: {
        services: { change: 12, trend: 'up' },
        revenue: { change: 8, trend: 'up' },
        rating: { change: 2, trend: 'up' },
        satisfaction: { change: -1, trend: 'down' },
        efficiency: { change: 5, trend: 'up' },
      },
    },
    {
      staffId: '2',
      month: '2024-11',
      metrics: {
        totalServices: 134,
        totalRevenue: 98000,
        averageRating: 4.6,
        customerSatisfaction: 88,
        repeatCustomers: 65,
        newCustomers: 18,
        workingHours: 160,
        efficiency: 84,
      },
      comparison: {
        services: { change: 8, trend: 'up' },
        revenue: { change: 15, trend: 'up' },
        rating: { change: 0, trend: 'stable' },
        satisfaction: { change: 3, trend: 'up' },
        efficiency: { change: 2, trend: 'up' },
      },
    },
    {
      staffId: '3',
      month: '2024-11',
      metrics: {
        totalServices: 142,
        totalRevenue: 112000,
        averageRating: 4.7,
        customerSatisfaction: 91,
        repeatCustomers: 72,
        newCustomers: 20,
        workingHours: 165,
        efficiency: 86,
      },
      comparison: {
        services: { change: -3, trend: 'down' },
        revenue: { change: 5, trend: 'up' },
        rating: { change: 1, trend: 'up' },
        satisfaction: { change: 0, trend: 'stable' },
        efficiency: { change: -2, trend: 'down' },
      },
    },
    {
      staffId: '4',
      month: '2024-11',
      metrics: {
        totalServices: 98,
        totalRevenue: 78000,
        averageRating: 4.5,
        customerSatisfaction: 87,
        repeatCustomers: 58,
        newCustomers: 15,
        workingHours: 140,
        efficiency: 70,
      },
      comparison: {
        services: { change: 18, trend: 'up' },
        revenue: { change: 22, trend: 'up' },
        rating: { change: 3, trend: 'up' },
        satisfaction: { change: 5, trend: 'up' },
        efficiency: { change: 8, trend: 'up' },
      },
    },
  ];

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return faArrowUp;
      case 'down':
        return faArrowDown;
      default:
        return faMinus;
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'up':
        return 'text-green-600';
      case 'down':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <FontAwesomeIcon key={i} icon={faStar} className={`w-4 h-4 ${i < Math.floor(rating) ? 'text-yellow-400' : 'text-gray-300'}`} />
    ));
  };

  const getPerformanceLevel = (efficiency: number) => {
    if (efficiency >= 90) return { level: 'Výborná', color: 'text-green-600', bgColor: 'bg-green-100' };
    if (efficiency >= 80) return { level: 'Dobrá', color: 'text-blue-600', bgColor: 'bg-blue-100' };
    if (efficiency >= 70) return { level: 'Průměrná', color: 'text-yellow-600', bgColor: 'bg-yellow-100' };
    return { level: 'Slabá', color: 'text-red-600', bgColor: 'bg-red-100' };
  };

  const topPerformer = performanceData.reduce((top, current) => (current.metrics.efficiency > top.metrics.efficiency ? current : top));

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Výkonnost" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Header */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Výkonnost zaměstnanců</h1>
                <p className="text-gray-600 mt-1">Analýza výkonnosti a metrik zaměstnanců</p>
              </div>
              <div className="flex items-center space-x-3">
                <select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="month">Tento měsíc</option>
                  <option value="quarter">Toto čtvrtletí</option>
                  <option value="year">Tento rok</option>
                </select>
                <button className="border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                  <FontAwesomeIcon icon={faDownload} className="mr-2" />
                  Export
                </button>
              </div>
            </div>
          </section>

          {/* Top Performer */}
          <section className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg shadow-sm p-6 border border-yellow-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
                  <FontAwesomeIcon icon={faTrophy} className="w-8 h-8 text-yellow-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Nejlepší výkonnost</h3>
                  <p className="text-gray-600">
                    {staff.find(s => s.id === topPerformer.staffId)?.name} - {topPerformer.metrics.efficiency}% efektivita
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-yellow-600">{topPerformer.metrics.totalRevenue.toLocaleString()} Kč</div>
                <div className="text-sm text-gray-600">Celkové tržby</div>
              </div>
            </div>
          </section>

          {/* Performance Grid */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {performanceData.map(data => {
              const staffMember = staff.find(s => s.id === data.staffId);
              const performance = getPerformanceLevel(data.metrics.efficiency);

              return (
                <div key={data.staffId} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-4">
                      <img src={staffMember?.avatar} alt={staffMember?.name} className="w-12 h-12 rounded-full" />
                      <div>
                        <h3 className="font-semibold text-gray-900">{staffMember?.name}</h3>
                        <p className="text-sm text-gray-600">{staffMember?.role}</p>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${performance.bgColor} ${performance.color}`}>{performance.level}</div>
                  </div>

                  {/* Key Metrics */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-gray-900">{data.metrics.totalServices}</div>
                      <div className="text-sm text-gray-600">Služby</div>
                      <div className="flex items-center justify-center mt-1">
                        <FontAwesomeIcon
                          icon={getTrendIcon(data.comparison.services.trend)}
                          className={`w-3 h-3 mr-1 ${getTrendColor(data.comparison.services.trend)}`}
                        />
                        <span className={`text-xs ${getTrendColor(data.comparison.services.trend)}`}>
                          {data.comparison.services.change > 0 ? '+' : ''}
                          {data.comparison.services.change}%
                        </span>
                      </div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-gray-900">{data.metrics.totalRevenue.toLocaleString()} Kč</div>
                      <div className="text-sm text-gray-600">Tržby</div>
                      <div className="flex items-center justify-center mt-1">
                        <FontAwesomeIcon icon={getTrendIcon(data.comparison.revenue.trend)} className={`w-3 h-3 mr-1 ${getTrendColor(data.comparison.revenue.trend)}`} />
                        <span className={`text-xs ${getTrendColor(data.comparison.revenue.trend)}`}>
                          {data.comparison.revenue.change > 0 ? '+' : ''}
                          {data.comparison.revenue.change}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Detailed Metrics */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faStar} className="w-4 h-4 mr-2 text-gray-400" />
                        <span className="text-sm text-gray-600">Hodnocení</span>
                      </div>
                      <div className="flex items-center">
                        <div className="flex mr-2">{renderStars(data.metrics.averageRating)}</div>
                        <span className="text-sm font-medium text-gray-900">{data.metrics.averageRating}/5</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faUsers} className="w-4 h-4 mr-2 text-gray-400" />
                        <span className="text-sm text-gray-600">Spokojenost zákazníků</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-900">{data.metrics.customerSatisfaction}%</span>
                        <FontAwesomeIcon
                          icon={getTrendIcon(data.comparison.satisfaction.trend)}
                          className={`w-3 h-3 ml-2 ${getTrendColor(data.comparison.satisfaction.trend)}`}
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faBullseye} className="w-4 h-4 mr-2 text-gray-400" />
                        <span className="text-sm text-gray-600">Efektivita</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-900">{data.metrics.efficiency}%</span>
                        <FontAwesomeIcon
                          icon={getTrendIcon(data.comparison.efficiency.trend)}
                          className={`w-3 h-3 ml-2 ${getTrendColor(data.comparison.efficiency.trend)}`}
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faClock} className="w-4 h-4 mr-2 text-gray-400" />
                        <span className="text-sm text-gray-600">Pracovní hodiny</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900">{data.metrics.workingHours}h</span>
                    </div>
                  </div>

                  {/* Customer Stats */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <div className="text-lg font-bold text-blue-600">{data.metrics.repeatCustomers}</div>
                        <div className="text-xs text-gray-600">Věrní zákazníci</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-600">{data.metrics.newCustomers}</div>
                        <div className="text-xs text-gray-600">Noví zákazníci</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </section>

          {/* Performance Summary */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Souhrn výkonnosti</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">
                  {Math.round(performanceData.reduce((sum, d) => sum + d.metrics.totalServices, 0) / performanceData.length)}
                </div>
                <div className="text-sm text-gray-600">Průměr služeb</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">
                  {Math.round(performanceData.reduce((sum, d) => sum + d.metrics.totalRevenue, 0) / performanceData.length).toLocaleString()} Kč
                </div>
                <div className="text-sm text-gray-600">Průměr tržeb</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">
                  {(performanceData.reduce((sum, d) => sum + d.metrics.averageRating, 0) / performanceData.length).toFixed(1)}
                </div>
                <div className="text-sm text-gray-600">Průměrné hodnocení</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">
                  {Math.round(performanceData.reduce((sum, d) => sum + d.metrics.efficiency, 0) / performanceData.length)}%
                </div>
                <div className="text-sm text-gray-600">Průměrná efektivita</div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default StaffPerformancePage;
